import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { 
  AlertTriangle, CheckCircle2, RefreshCw, Flame, 
  Clock, User, Settings, Bell, Shield, Activity,
  TrendingUp, TrendingDown, Minus, Target, Calendar,
  Zap, ChevronRight, Loader2, LineChart, BarChart3
} from 'lucide-react';
import {
  LineChart as ReLineChart, Line, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer, ReferenceLine, Area, AreaChart
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SeverityBadge = ({ severity }) => {
  const styles = {
    emergency: 'bg-red-100 text-red-700 border-red-200',
    critical: 'bg-orange-100 text-orange-700 border-orange-200',
    warning: 'bg-yellow-100 text-yellow-700 border-yellow-200'
  };
  
  const icons = {
    emergency: <Flame className="h-3 w-3" />,
    critical: <AlertTriangle className="h-3 w-3" />,
    warning: <Bell className="h-3 w-3" />
  };
  
  return (
    <Badge className={`${styles[severity] || styles.warning} flex items-center gap-1`}>
      {icons[severity]}
      {severity?.toUpperCase()}
    </Badge>
  );
};

export default function BurnoutAlerts({ token }) {
  const [loading, setLoading] = useState(true);
  const [forecastLoading, setForecastLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('current');
  const [dashboard, setDashboard] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [showConfig, setShowConfig] = useState(false);
  const [config, setConfig] = useState({
    enabled: true,
    thresholds: { warning: 13, critical: 15, emergency: 18 },
    notification_methods: ['ui', 'email'],
    cooldown_hours: 24
  });

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/burnout-alerts/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDashboard(data);
        if (data.thresholds) {
          setConfig(prev => ({ ...prev, thresholds: data.thresholds }));
        }
      }
    } catch (err) {
      console.error('Failed to fetch burnout dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchForecast = async () => {
    setForecastLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/burnout-alerts/forecast`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setForecast(data);
        toast.success('Forecast generated successfully');
      } else {
        toast.error('Failed to generate forecast');
      }
    } catch (err) {
      toast.error('Error generating forecast');
    } finally {
      setForecastLoading(false);
    }
  };

  const runManualCheck = async () => {
    try {
      const res = await fetch(`${API_URL}/api/burnout-alerts/check`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const result = await res.json();
        toast.success(`Check complete: ${result.at_risk_count} at risk, ${result.alerts_created} new alerts`);
        fetchDashboard();
      }
    } catch (err) {
      toast.error('Failed to run burnout check');
    }
  };

  const acknowledgeAlert = async (alertId) => {
    try {
      const res = await fetch(`${API_URL}/api/burnout-alerts/${alertId}/acknowledge`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Alert acknowledged');
        fetchDashboard();
      }
    } catch (err) {
      toast.error('Failed to acknowledge alert');
    }
  };

  const resolveAlert = async (alertId) => {
    try {
      const res = await fetch(`${API_URL}/api/burnout-alerts/${alertId}/resolve`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ notes: 'Resolved via dashboard' })
      });
      if (res.ok) {
        toast.success('Alert resolved');
        fetchDashboard();
      }
    } catch (err) {
      toast.error('Failed to resolve alert');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  const assessment = dashboard?.current_assessment || {};
  const stats = dashboard?.statistics || {};
  const alerts = dashboard?.recent_alerts || [];

  // Prepare chart data from forecast
  const agencyChartData = forecast?.agency_forecast?.weekly_summary?.map((week) => ({
    name: `Week ${week.week}`,
    adc: week.projected_adc,
    avgCaseload: week.avg_caseload,
    atRisk: week.at_risk_count,
    staffGap: Math.max(0, week.staff_gap)
  })) || [];

  return (
    <div className="space-y-6" data-testid="burnout-alerts-component">
      {/* Header Actions */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-800">Burnout Risk Monitoring</h3>
          <p className="text-sm text-slate-500">Automated alerts and predictive forecasting for RNCM caseloads</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => setShowConfig(!showConfig)}>
            <Settings className="h-4 w-4 mr-2" />
            Configure
          </Button>
          <Button size="sm" onClick={runManualCheck} data-testid="run-check-btn">
            <RefreshCw className="h-4 w-4 mr-2" />
            Run Check
          </Button>
        </div>
      </div>

      {/* Configuration Panel */}
      {showConfig && (
        <Card className="border-blue-200 bg-blue-50/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-blue-800">Alert Thresholds</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-6">
              <div>
                <Label className="text-xs text-slate-500">Warning Threshold</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input 
                    type="number" 
                    value={config.thresholds.warning} 
                    className="w-20" 
                    disabled 
                  />
                  <span className="text-xs text-slate-500">patients/RNCM</span>
                </div>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Critical Threshold</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input 
                    type="number" 
                    value={config.thresholds.critical} 
                    className="w-20" 
                    disabled 
                  />
                  <span className="text-xs text-slate-500">patients/RNCM</span>
                </div>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Emergency Threshold</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input 
                    type="number" 
                    value={config.thresholds.emergency} 
                    className="w-20" 
                    disabled 
                  />
                  <span className="text-xs text-slate-500">patients/RNCM</span>
                </div>
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-3">Contact admin to modify thresholds</p>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="border-slate-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 font-medium">Total Clinicians</p>
                <p className="text-2xl font-bold text-slate-800">{assessment.total_clinicians || 0}</p>
              </div>
              <div className="h-10 w-10 bg-slate-100 rounded-lg flex items-center justify-center">
                <User className="h-5 w-5 text-slate-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={`${assessment.at_risk_count > 0 ? 'border-red-200 bg-red-50/50' : 'border-emerald-200 bg-emerald-50/50'}`}>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 font-medium">At Risk Now</p>
                <p className={`text-2xl font-bold ${assessment.at_risk_count > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                  {assessment.at_risk_count || 0}
                </p>
              </div>
              <div className={`h-10 w-10 ${assessment.at_risk_count > 0 ? 'bg-red-100' : 'bg-emerald-100'} rounded-lg flex items-center justify-center`}>
                {assessment.at_risk_count > 0 ? (
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                ) : (
                  <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 font-medium">Alerts (7d)</p>
                <p className="text-2xl font-bold text-slate-800">{stats.alerts_7d || 0}</p>
              </div>
              <div className="h-10 w-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Bell className="h-5 w-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 font-medium">Unresolved</p>
                <p className={`text-2xl font-bold ${stats.unresolved_count > 0 ? 'text-orange-600' : 'text-slate-800'}`}>
                  {stats.unresolved_count || 0}
                </p>
              </div>
              <div className="h-10 w-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <Clock className="h-5 w-5 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for Current vs Predictive */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-3 w-full max-w-md">
          <TabsTrigger value="current" data-testid="tab-current">
            <Activity className="h-4 w-4 mr-1" /> Current
          </TabsTrigger>
          <TabsTrigger value="forecast" data-testid="tab-forecast">
            <TrendingUp className="h-4 w-4 mr-1" /> Predictive
          </TabsTrigger>
          <TabsTrigger value="alerts" data-testid="tab-alerts">
            <Bell className="h-4 w-4 mr-1" /> Alerts
          </TabsTrigger>
        </TabsList>

        {/* Current Tab */}
        <TabsContent value="current" className="space-y-4">
          {/* At Risk Clinicians */}
          {assessment.at_risk_clinicians?.length > 0 ? (
            <Card className="border-red-200">
              <CardHeader className="pb-3 bg-red-50/50 border-b border-red-100">
                <CardTitle className="text-sm font-semibold text-red-800 flex items-center gap-2">
                  <Flame className="h-4 w-4" />
                  Currently At Risk ({assessment.at_risk_clinicians.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="space-y-3">
                  {assessment.at_risk_clinicians.map((clinician, idx) => (
                    <div 
                      key={idx} 
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        clinician.severity === 'emergency' ? 'bg-red-50 border-red-200' :
                        clinician.severity === 'critical' ? 'bg-orange-50 border-orange-200' :
                        'bg-yellow-50 border-yellow-200'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`h-8 w-8 rounded-full flex items-center justify-center ${
                          clinician.severity === 'emergency' ? 'bg-red-100' :
                          clinician.severity === 'critical' ? 'bg-orange-100' :
                          'bg-yellow-100'
                        }`}>
                          <User className={`h-4 w-4 ${
                            clinician.severity === 'emergency' ? 'text-red-600' :
                            clinician.severity === 'critical' ? 'text-orange-600' :
                            'text-yellow-600'
                          }`} />
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">{clinician.name}</p>
                          <p className="text-xs text-slate-500">{clinician.planning_outcome}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className={`text-lg font-bold ${
                            clinician.severity === 'emergency' ? 'text-red-600' :
                            clinician.severity === 'critical' ? 'text-orange-600' :
                            'text-yellow-600'
                          }`}>
                            {clinician.caseload}
                          </p>
                          <p className="text-xs text-slate-500">patients</p>
                        </div>
                        <SeverityBadge severity={clinician.severity} />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-emerald-200 bg-emerald-50/30">
              <CardContent className="py-12 text-center">
                <Shield className="h-12 w-12 mx-auto mb-3 text-emerald-400" />
                <p className="font-medium text-emerald-700">All Clear</p>
                <p className="text-sm text-emerald-600">No clinicians currently at risk</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Predictive Forecast Tab */}
        <TabsContent value="forecast" className="space-y-4">
          {/* Generate Forecast Button */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <LineChart className="h-4 w-4 text-[#00b0ab]" />
                    4-Week Burnout Forecast
                  </CardTitle>
                  <CardDescription>
                    Predict which clinicians will hit burnout thresholds based on admission trends
                  </CardDescription>
                </div>
                <Button 
                  onClick={fetchForecast} 
                  disabled={forecastLoading}
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="generate-forecast-btn"
                >
                  {forecastLoading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <TrendingUp className="h-4 w-4 mr-2" />
                  )}
                  Generate Forecast
                </Button>
              </div>
            </CardHeader>
          </Card>

          {forecast ? (
            <>
              {/* Forecast Summary */}
              <div className="grid grid-cols-4 gap-4">
                <Card className="border-indigo-200 bg-[#00b0ab]/10/50">
                  <CardContent className="pt-4">
                    <p className="text-xs text-[#00b0ab] font-medium">Forecast Period</p>
                    <p className="text-lg font-bold text-indigo-800">{forecast.forecast_period?.weeks || 4} Weeks</p>
                    <p className="text-xs text-[#00b0ab]">
                      {forecast.forecast_period?.start} → {forecast.forecast_period?.end}
                    </p>
                  </CardContent>
                </Card>

                <Card className={`border-slate-200 ${forecast.current_state?.at_risk_now > 0 ? 'border-amber-200' : ''}`}>
                  <CardContent className="pt-4">
                    <p className="text-xs text-slate-500 font-medium">Currently At Risk</p>
                    <p className={`text-2xl font-bold ${forecast.current_state?.at_risk_now > 0 ? 'text-amber-600' : 'text-slate-800'}`}>
                      {forecast.current_state?.at_risk_now || 0}
                    </p>
                    <p className="text-xs text-slate-400">of {forecast.current_state?.total_clinicians || 0} clinicians</p>
                  </CardContent>
                </Card>

                <Card className={`border-slate-200 ${forecast.risk_summary?.clinicians_will_burnout > 0 ? 'border-red-200 bg-red-50/30' : ''}`}>
                  <CardContent className="pt-4">
                    <p className="text-xs text-slate-500 font-medium">Will Hit Burnout</p>
                    <p className={`text-2xl font-bold ${forecast.risk_summary?.clinicians_will_burnout > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                      {forecast.risk_summary?.clinicians_will_burnout || 0}
                    </p>
                    <p className="text-xs text-slate-400">within {forecast.forecast_period?.weeks || 4} weeks</p>
                  </CardContent>
                </Card>

                <Card className="border-slate-200">
                  <CardContent className="pt-4">
                    <p className="text-xs text-slate-500 font-medium">Confidence</p>
                    <p className="text-2xl font-bold text-slate-800">{forecast.confidence?.overall_score || 0}%</p>
                    <Badge variant="outline" className={`text-xs ${
                      forecast.confidence?.level === 'High' ? 'border-emerald-500 text-emerald-600' :
                      forecast.confidence?.level === 'Moderate' ? 'border-amber-500 text-amber-600' :
                      'border-red-500 text-red-600'
                    }`}>
                      {forecast.confidence?.level || 'Unknown'}
                    </Badge>
                  </CardContent>
                </Card>
              </div>

              {/* Trend Indicators */}
              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-emerald-500" />
                      Admission Trend
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-4">
                      <div>
                        <p className="text-2xl font-bold">{forecast.admission_trend?.weekly_avg || 0}</p>
                        <p className="text-xs text-slate-500">avg weekly admissions</p>
                      </div>
                      <Badge className={`${
                        forecast.admission_trend?.trend === 'increasing' ? 'bg-emerald-100 text-emerald-700' :
                        forecast.admission_trend?.trend === 'decreasing' ? 'bg-red-100 text-red-700' :
                        'bg-slate-100 text-slate-700'
                      }`}>
                        {forecast.admission_trend?.trend === 'increasing' && <TrendingUp className="h-3 w-3 mr-1" />}
                        {forecast.admission_trend?.trend === 'decreasing' && <TrendingDown className="h-3 w-3 mr-1" />}
                        {forecast.admission_trend?.trend === 'stable' && <Minus className="h-3 w-3 mr-1" />}
                        {forecast.admission_trend?.change_rate > 0 ? '+' : ''}{forecast.admission_trend?.change_rate || 0}%
                      </Badge>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <TrendingDown className="h-4 w-4 text-red-500" />
                      Discharge Trend
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-4">
                      <div>
                        <p className="text-2xl font-bold">{forecast.discharge_trend?.weekly_avg || 0}</p>
                        <p className="text-xs text-slate-500">avg weekly discharges</p>
                      </div>
                      <Badge className={`${
                        forecast.discharge_trend?.trend === 'increasing' ? 'bg-emerald-100 text-emerald-700' :
                        forecast.discharge_trend?.trend === 'decreasing' ? 'bg-red-100 text-red-700' :
                        'bg-slate-100 text-slate-700'
                      }`}>
                        {forecast.discharge_trend?.trend === 'increasing' && <TrendingUp className="h-3 w-3 mr-1" />}
                        {forecast.discharge_trend?.trend === 'decreasing' && <TrendingDown className="h-3 w-3 mr-1" />}
                        {forecast.discharge_trend?.trend === 'stable' && <Minus className="h-3 w-3 mr-1" />}
                        {forecast.discharge_trend?.change_rate > 0 ? '+' : ''}{forecast.discharge_trend?.change_rate || 0}%
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Agency Projection Chart */}
              {agencyChartData.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <BarChart3 className="h-4 w-4 text-[#00b0ab]" />
                      4-Week Agency Projection
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={agencyChartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                          <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                          <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
                          <Tooltip 
                            contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                          />
                          <Legend />
                          <ReferenceLine yAxisId="left" y={15} stroke="#ef4444" strokeDasharray="5 5" label="Burnout Threshold" />
                          <Area 
                            yAxisId="left"
                            type="monotone" 
                            dataKey="avgCaseload" 
                            stroke="#6366f1" 
                            fill="#c7d2fe" 
                            name="Avg Caseload"
                          />
                          <Line 
                            yAxisId="right"
                            type="monotone" 
                            dataKey="atRisk" 
                            stroke="#f59e0b" 
                            strokeWidth={2}
                            name="At Risk Count"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Clinician Forecasts */}
              {forecast.clinician_forecasts?.length > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2">
                      <User className="h-4 w-4 text-[#00b0ab]" />
                      Individual Clinician Forecasts
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {forecast.clinician_forecasts.map((cf, idx) => (
                        <div key={idx} className={`p-3 rounded-lg border ${
                          cf.burnout_week ? 'border-red-200 bg-red-50/30' :
                          cf.current_risk !== 'safe' ? 'border-amber-200 bg-amber-50/30' :
                          'border-slate-200'
                        }`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`h-8 w-8 rounded-full flex items-center justify-center ${
                                cf.burnout_week ? 'bg-red-100' :
                                cf.current_risk !== 'safe' ? 'bg-amber-100' :
                                'bg-slate-100'
                              }`}>
                                <User className={`h-4 w-4 ${
                                  cf.burnout_week ? 'text-red-600' :
                                  cf.current_risk !== 'safe' ? 'text-amber-600' :
                                  'text-slate-600'
                                }`} />
                              </div>
                              <div>
                                <p className="font-medium text-slate-800">{cf.clinician_name}</p>
                                <div className="flex items-center gap-2 text-xs text-slate-500">
                                  <span>Current: {cf.current_caseload} pts</span>
                                  <span>→</span>
                                  <span>Max: {cf.max_projected_caseload} pts</span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              {cf.burnout_week && (
                                <Badge className="bg-red-100 text-red-700">
                                  <Flame className="h-3 w-3 mr-1" />
                                  Burnout Week {cf.burnout_week}
                                </Badge>
                              )}
                              <Badge className={`${
                                cf.trend === 'increasing' ? 'bg-amber-100 text-amber-700' :
                                cf.trend === 'decreasing' ? 'bg-emerald-100 text-emerald-700' :
                                'bg-slate-100 text-slate-700'
                              }`}>
                                {cf.trend === 'increasing' && <TrendingUp className="h-3 w-3 mr-1" />}
                                {cf.trend === 'decreasing' && <TrendingDown className="h-3 w-3 mr-1" />}
                                {cf.trend === 'stable' && <Minus className="h-3 w-3 mr-1" />}
                                {cf.trend}
                              </Badge>
                            </div>
                          </div>
                          
                          {/* Weekly progression bar */}
                          <div className="mt-3 grid grid-cols-4 gap-2">
                            {cf.weekly_forecasts?.map((wf, wIdx) => (
                              <div 
                                key={wIdx} 
                                className={`p-2 rounded text-center text-xs ${
                                  wf.risk_level === 'emergency' ? 'bg-red-100 text-red-700' :
                                  wf.risk_level === 'critical' ? 'bg-orange-100 text-orange-700' :
                                  wf.risk_level === 'warning' ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-emerald-50 text-emerald-700'
                                }`}
                              >
                                <p className="font-medium">Wk {wf.week}</p>
                                <p className="font-bold">{wf.projected_caseload}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Recommendations */}
              {forecast.recommendations?.length > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2">
                      <Zap className="h-4 w-4 text-[#00b0ab]" />
                      AI Recommendations
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {forecast.recommendations.map((rec, idx) => (
                        <div 
                          key={idx} 
                          className={`p-4 rounded-lg border ${
                            rec.priority === 'CRITICAL' ? 'bg-red-50 border-red-200' :
                            rec.priority === 'HIGH' ? 'bg-amber-50 border-amber-200' :
                            rec.priority === 'MEDIUM' ? 'bg-blue-50 border-blue-200' :
                            'bg-slate-50 border-slate-200'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <Badge className={`shrink-0 ${
                              rec.priority === 'CRITICAL' ? 'bg-red-100 text-red-700' :
                              rec.priority === 'HIGH' ? 'bg-amber-100 text-amber-700' :
                              rec.priority === 'MEDIUM' ? 'bg-blue-100 text-blue-700' :
                              'bg-slate-100 text-slate-700'
                            }`}>
                              {rec.priority}
                            </Badge>
                            <div className="flex-1">
                              <p className="font-medium text-slate-800">{rec.title}</p>
                              <p className="text-sm text-slate-600 mt-1">{rec.action}</p>
                              {rec.impact && (
                                <p className="text-xs text-slate-500 mt-2">
                                  <ChevronRight className="h-3 w-3 inline" /> {rec.impact}
                                </p>
                              )}
                              {rec.timeline && (
                                <Badge variant="outline" className="mt-2 text-xs">
                                  <Calendar className="h-3 w-3 mr-1" />
                                  {rec.timeline}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Confidence Factors */}
              {forecast.confidence?.factors?.length > 0 && (
                <Card className="border-slate-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Forecast Reliability</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {forecast.confidence.factors.map((factor, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm text-slate-600">
                          <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                          {factor}
                        </div>
                      ))}
                    </div>
                    <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t">
                      <div>
                        <p className="text-xs text-slate-500">Data Availability</p>
                        <Progress value={forecast.confidence?.breakdown?.data_availability || 0} className="h-2 mt-1" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Trend Stability</p>
                        <Progress value={forecast.confidence?.breakdown?.trend_stability || 0} className="h-2 mt-1" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Sample Size</p>
                        <Progress value={forecast.confidence?.breakdown?.sample_size || 0} className="h-2 mt-1" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card className="border-indigo-200 bg-[#00b0ab]/10/30">
              <CardContent className="py-12 text-center">
                <TrendingUp className="h-12 w-12 mx-auto mb-3 text-indigo-300" />
                <p className="font-medium text-indigo-700">No Forecast Generated</p>
                <p className="text-sm text-[#00b0ab]">Click "Generate Forecast" to see 4-week projections</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Alerts Tab */}
        <TabsContent value="alerts" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold text-slate-800">Recent Alerts</CardTitle>
            </CardHeader>
            <CardContent>
              {alerts.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Shield className="h-12 w-12 mx-auto mb-3 text-emerald-300" />
                  <p className="font-medium text-emerald-600">No active alerts</p>
                  <p className="text-sm">All clinicians are within safe caseload limits</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {alerts.slice(0, 10).map((alert, idx) => (
                    <div 
                      key={idx} 
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        alert.acknowledged ? 'bg-slate-50 border-slate-200' : 'bg-white border-slate-200'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <SeverityBadge severity={alert.severity} />
                        <div>
                          <p className="font-medium text-slate-800">{alert.clinician_name}</p>
                          <p className="text-xs text-slate-500">{alert.message}</p>
                          <p className="text-xs text-slate-400">
                            {new Date(alert.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {!alert.acknowledged && (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => acknowledgeAlert(alert.id)}
                          >
                            Acknowledge
                          </Button>
                        )}
                        {!alert.resolved && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                            onClick={() => resolveAlert(alert.id)}
                          >
                            Resolve
                          </Button>
                        )}
                        {alert.resolved && (
                          <Badge className="bg-emerald-100 text-emerald-700">Resolved</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
